import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Group,
  Text,
  TextInput,
  Stack,
  Avatar,
  Badge,
  Select,
  Alert,
  Box,
  Divider,
  ActionIcon,
  PasswordInput,
  CopyButton,
  Card,
  ScrollArea,
  Tooltip,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import {
  IconCircleCheck,
  IconCircleX,
  IconRotateClockwise,
  IconCopy,
  IconTrash,
  IconInfoCircle,
  IconCheck,
} from '@tabler/icons-react';
import { usePermissionControl } from '../hooks/usePermissionControl';
import { isCompanyLevelRole, NO_DEPARTMENT_ID } from '../utils';

// 翻译部门路径中的未分配文本
const translateDepartmentPath = (departmentPath: string, t: any) => {
  if (departmentPath === '__UNASSIGNED__') {
    return t('adminConsole.newConsole.unassigned') || 'Unassigned';
  }
  // 替换路径中的 __UNASSIGNED__ 标识符
  return departmentPath.replace(/__UNASSIGNED__/g, t('adminConsole.newConsole.unassigned') || 'Unassigned');
};

// 获取权限文本翻译
const getPermissionText = (roleCode: string, t: any) => {
  switch (roleCode) {
    case 'ROLE_COMPANY_OWNER':
      return t('ROLE_COMPANY_OWNER') || 'Company Owner';
    case 'ROLE_ADMIN':
      return t('ROLE_ADMIN') || 'Administrator';
    case 'ROLE_ADMIN_DEPARTMENT':
      return t('ROLE_ADMIN_DEPARTMENT') || 'Department Admin';
    case 'ROLE_EDITOR':
      return t('ROLE_EDITOR') || 'Editor';
    case 'ROLE_VIEWER':
      return t('ROLE_VIEWER') || 'Viewer';
    default:
      return t('ROLE_VIEWER') || 'Viewer';
  }
};

// 获取所有部门列表
const getAllDepartments = (nodes: any[], path: string[] = []): Array<{ path: string; id: string; isLeaf: boolean }> => {
  let departments: Array<{ path: string; id: string; isLeaf: boolean }> = [];

  for (const node of nodes) {
    // 如果是根节点（通常id为'root'），直接用node.name作为起始路径
    // 否则，继承父路径
    const currentPath = node.id === 'root' ? [node.name] : [...path, node.name];
    const pathString = currentPath.join(' > ');
    const isLeaf = !node.children || node.children.length === 0;

    // 根节点也要添加到部门列表中
    departments.push({ path: pathString, id: node.id, isLeaf });

    if (node.children && node.children.length > 0) {
      departments = departments.concat(getAllDepartments(node.children, currentPath));
    }
  }

  return departments;
};

// 过滤可选择的部门
const getFilteredDepartments = (
  allDepartments: Array<{ path: string; id: string; isLeaf: boolean }>,
  permissionControl: any
) => {
  console.log('getFilteredDepartments 调试信息:', {
    allDepartmentsCount: allDepartments.length,
    isOwner: permissionControl.isOwner,
    isAdmin: permissionControl.isAdmin,
    currentUserRole: permissionControl.currentUserRole,
    currentUserPath: permissionControl.currentUser?.departmentPath,
    currentUser: permissionControl.currentUser,
  });

  // 公司所有者和公司管理员可以选择所有部门
  if (permissionControl.isOwner || permissionControl.isAdmin) {
    console.log('用户是所有者或管理员，返回所有部门');
    return allDepartments.filter((dept) => dept.id !== 'root'); // 根节点不可选择
  }

  // 部门管理员只能选择自己所属部门及其子部门
  if (permissionControl.currentUserRole === 'ROLE_ADMIN_DEPARTMENT') {
    const currentUserPath = permissionControl.currentUser?.departmentPath;
    console.log('部门管理员权限检查:', { currentUserPath });

    if (!currentUserPath) {
      console.log('当前用户没有部门路径，返回空数组');
      return [];
    }

    const filteredDepts = allDepartments.filter((dept) => {
      // 根节点不可选择
      if (dept.id === 'root') return false;

      // 检查部门是否是当前用户部门或其子部门
      const isMatch = dept.path.startsWith(currentUserPath);
      console.log('部门匹配检查:', { deptPath: dept.path, currentUserPath, isMatch });
      return isMatch;
    });

    console.log('过滤后的部门列表:', filteredDepts);
    return filteredDepts;
  }

  console.log('用户角色不匹配，返回空数组');
  return [];
};

interface MemberEditDialogsProps {
  editingMember: any;
  showEditMemberDialog: boolean;
  showStatusDialog: boolean;
  showApiBindingDialog: boolean;
  showDeptPermissionDialog: boolean;
  showPasswordResetDialog: boolean;
  organizationData: any[];
  roles: any[]; // 添加roles数据
  loadAssignableRoles: () => Promise<any[]>; // 添加可分配角色加载方法
  onCloseEditMember: () => void;
  onCloseStatus: () => void;
  onCloseApiBinding: () => void;
  onCloseDeptPermission: () => void;
  onClosePasswordReset: () => void;
  onUpdateMember: (member: any) => Promise<boolean>;
  onAssignUserToDepartment?: (userId: string, departmentId: string) => Promise<any>;
  onUpdateUserDepartmentAndRole?: (userIds: string[], departmentId: string, sysRoleId: string) => Promise<boolean>;
  onResetPassword?: (userId: string) => Promise<string | null>;
  onToggleUserStatus?: (userId: string, enabled: boolean, departmentId?: string) => Promise<boolean>;
  onGetUserApiKeys?: (userUuid: string) => Promise<any[]>;
  onAddUserApiKey?: (userUuid: string, apiKey: string, description?: string) => Promise<boolean>;
  onDeleteUserApiKey?: (userUuid: string, apiKey: string) => Promise<boolean>;
}

const MemberEditDialogs: React.FC<MemberEditDialogsProps> = ({
  editingMember,
  showEditMemberDialog,
  showStatusDialog,
  showApiBindingDialog,
  showDeptPermissionDialog,
  showPasswordResetDialog,
  organizationData,
  roles,
  loadAssignableRoles,
  onCloseEditMember,
  onCloseStatus,
  onCloseApiBinding,
  onCloseDeptPermission,
  onClosePasswordReset,
  onUpdateMember,
  onAssignUserToDepartment,
  onUpdateUserDepartmentAndRole,
  onToggleUserStatus,
  onResetPassword,
  onGetUserApiKeys,
  onAddUserApiKey,
  onDeleteUserApiKey,
}) => {
  const { t } = useTranslation();
  const permissionControl = usePermissionControl();
  const [tempMember, setTempMember] = useState<any>(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [userApiKeys, setUserApiKeys] = useState<string[]>([]);
  const [assignableRolesForEdit, setAssignableRolesForEdit] = useState<any[]>([]);
  const [loadingAssignableRoles, setLoadingAssignableRoles] = useState(false);

  // 提取tempMember初始化逻辑为函数
  const initializeTempMember = (memberData: any) => {
    let initialMember = { ...memberData };

    // 如果没有sysRoleId但有permissionLevel，需要根据permissionLevel推算sysRoleId
    if (!initialMember.sysRoleId && initialMember.permissionLevel !== undefined) {
      const permissionLevelToRoleCode = {
        1: 'ROLE_COMPANY_OWNER',
        2: 'ROLE_ADMIN',
        3: 'ROLE_ADMIN_DEPARTMENT',
        4: 'ROLE_EDITOR',
        5: 'ROLE_VIEWER',
      };

      const roleCode =
        permissionLevelToRoleCode[initialMember.permissionLevel as keyof typeof permissionLevelToRoleCode];
      if (roleCode) {
        const role = roles.find((r) => r.code === roleCode);
        if (role) {
          initialMember.sysRoleId = role.id;
        }
      }
    }

    return initialMember;
  };

  useEffect(() => {
    if (editingMember) {
      const initialMember = initializeTempMember(editingMember);
      setTempMember(initialMember);
    }
  }, [editingMember, roles]);

  // 加载用户的API密钥
  useEffect(() => {
    if (showApiBindingDialog && editingMember && onGetUserApiKeys) {
      setLoadingApiKeys(true);
      // 使用uuid作为用户唯一标识符
      const userUuid = editingMember.uuid || editingMember.id.toString();
      onGetUserApiKeys(userUuid)
        .then((keys) => {
          // 处理返回的密钥数据，提取api_key字段
          const keyStrings = keys.map((k: any) => k.api_key || k.apiKey || k);
          setUserApiKeys(keyStrings);
          setTempMember((prev: any) => ({
            ...prev,
            apiKeys: keyStrings,
            apiKeyBound: keyStrings.length > 0,
          }));
        })
        .catch((error) => {
          console.error('加载API密钥失败:', error);
          setUserApiKeys([]);
        })
        .finally(() => {
          setLoadingApiKeys(false);
        });
    }
  }, [showApiBindingDialog, editingMember, onGetUserApiKeys]);

  // 加载可分配角色当部门权限对话框打开时
  useEffect(() => {
    if (showDeptPermissionDialog) {
      // 重置 tempMember 为当前编辑的用户
      if (editingMember) {
        const initialMember = initializeTempMember(editingMember);
        setTempMember(initialMember);
      }

      const loadRoles = async () => {
        setLoadingAssignableRoles(true);
        try {
          const assignableRoles = await loadAssignableRoles();
          setAssignableRolesForEdit(assignableRoles);
        } catch (error) {
          console.error('加载可分配角色失败:', error);
          setAssignableRolesForEdit([]);
        } finally {
          setLoadingAssignableRoles(false);
        }
      };
      loadRoles();
    }
  }, [showDeptPermissionDialog, editingMember, loadAssignableRoles]);

  const allDepartments = getAllDepartments(organizationData);

  // 根据权限过滤部门
  const filteredDepartments = getFilteredDepartments(allDepartments, permissionControl);

  // 部门权限对话框中，当前选中角色是否为公司级角色（公司所有者 / 公司管理员）——此类角色部门可不选
  const editSelectedRoleCode = assignableRolesForEdit.find(
    (r) => r.id.toString() === tempMember?.sysRoleId?.toString()
  )?.code;
  const editDepartmentOptional = isCompanyLevelRole(editSelectedRoleCode);

  // 编辑成员信息
  const handleSaveMember = async () => {
    if (!tempMember) return;
    const success = await onUpdateMember(tempMember);
    if (success) {
      notifications.show({
        message: t('adminConsole.newConsole.member.updateSuccess'),
        color: 'green',
      });
      onCloseEditMember();
    }
    // 失败时由 onUpdateMember 内部显示错误通知，这里不再显示
  };

  // 切换状态
  const handleToggleStatus = async () => {
    if (!tempMember) return;
    const newEnabled = !tempMember.enabled;

    // Cloud 模式下使用 /user/company/set 和 /user/company/unset 接口
    const isCloudMode = import.meta.env.VITE_MODE !== 'local';
    let success = false;

    if (isCloudMode && onToggleUserStatus) {
      // 启用时保留原部门归属，防止部门信息丢失
      success = await onToggleUserStatus(tempMember.id, newEnabled, tempMember.departmentId);
    } else {
      const updatedMember = {
        ...tempMember,
        status: '', // 保持为空，渲染时实时翻译
        enabled: newEnabled,
      };
      success = await onUpdateMember(updatedMember);
    }

    if (success) {
      onCloseStatus();
    }
    // 失败时由回调内部显示错误通知，这里不再显示
  };

  // 添加API密钥（手动输入并保存到后端）
  const handleAddApiKey = async () => {
    if (!tempMember || !newApiKey.trim()) return;

    if (onAddUserApiKey) {
      setLoadingApiKeys(true);
      try {
        const userUuid = tempMember.uuid || tempMember.id.toString();
        const success = await onAddUserApiKey(userUuid, newApiKey.trim());
        if (success) {
          // 重新加载API密钥列表
          if (onGetUserApiKeys) {
            const keys = await onGetUserApiKeys(userUuid);
            const keyStrings = keys.map((k: any) => k.api_key || k.apiKey || k);
            setUserApiKeys(keyStrings);
            setTempMember((prev: any) => ({
              ...prev,
              apiKeys: keyStrings,
              apiKeyBound: keyStrings.length > 0,
            }));
          }
          setNewApiKey('');
          notifications.show({
            message: t('adminConsole.newConsole.member.apiKey.addSuccess'),
            color: 'green',
          });
        }
      } catch (error: any) {
        console.error('添加API密钥失败:', error);
        notifications.show({
          message: t('adminConsole.newConsole.member.apiKey.addFailed'),
          color: 'red',
        });
      } finally {
        setLoadingApiKeys(false);
      }
    } else {
      // 如果没有提供后端方法，只更新本地状态
      const updatedKeys = [...(tempMember.apiKeys || []), newApiKey.trim()];
      const updatedMember = {
        ...tempMember,
        apiKeys: updatedKeys,
        apiKeyBound: true,
      };
      setTempMember(updatedMember);
      setUserApiKeys(updatedKeys);
      setNewApiKey('');
      onUpdateMember(updatedMember);
      notifications.show({
        message: t('adminConsole.newConsole.member.apiKey.addSuccess'),
        color: 'green',
      });
    }
  };

  // 删除API密钥
  const handleRemoveApiKey = async (keyToRemove: string) => {
    if (!tempMember) return;

    if (onDeleteUserApiKey) {
      setLoadingApiKeys(true);
      try {
        const userUuid = tempMember.uuid || tempMember.id.toString();
        const success = await onDeleteUserApiKey(userUuid, keyToRemove);
        if (success) {
          // 重新加载API密钥列表
          if (onGetUserApiKeys) {
            const keys = await onGetUserApiKeys(userUuid);
            const keyStrings = keys.map((k: any) => k.api_key || k.apiKey || k);
            setUserApiKeys(keyStrings);
            setTempMember((prev: any) => ({
              ...prev,
              apiKeys: keyStrings,
              apiKeyBound: keyStrings.length > 0,
            }));
          }
        }
      } catch (error) {
        console.error('删除API密钥失败:', error);
      } finally {
        setLoadingApiKeys(false);
      }
    } else {
      // 如果没有提供后端方法，只更新本地状态
      const updatedKeys = tempMember.apiKeys.filter((key: string) => key !== keyToRemove);
      const updatedMember = {
        ...tempMember,
        apiKeys: updatedKeys,
        apiKeyBound: updatedKeys.length > 0,
      };
      setTempMember(updatedMember);
      setUserApiKeys(updatedKeys);
      onUpdateMember(updatedMember);
      notifications.show({
        message: t('adminConsole.newConsole.member.apiKey.deleteSuccess'),
        color: 'green',
      });
    }
  };

  // 保存部门权限设置
  const handleSaveDeptPermission = async () => {
    if (!tempMember || !editingMember) return;

    // 验证权限等级是否已选择
    if (!tempMember.sysRoleId) {
      notifications.show({
        title: t('adminConsole.newConsole.validation.failed'),
        message: t('adminConsole.newConsole.addUser.validation.roleRequired'),
        color: 'red',
      });
      return;
    }

    // 公司级角色（公司所有者 / 公司管理员）允许不选部门
    const selectedRoleCode = assignableRolesForEdit.find(
      (r) => r.id.toString() === tempMember.sysRoleId?.toString()
    )?.code;
    const departmentOptional = isCompanyLevelRole(selectedRoleCode);

    // 已选择的真实部门 ID（排除 0 这种“无部门”占位）
    const selectedDepartmentId =
      tempMember.departmentId && tempMember.departmentId !== NO_DEPARTMENT_ID ? tempMember.departmentId : '';

    // 非公司级角色必须选择部门
    if (!selectedDepartmentId && !departmentOptional) {
      notifications.show({
        title: t('adminConsole.newConsole.validation.failed'),
        message: t('adminConsole.newConsole.member.departmentRequired'),
        color: 'red',
      });
      return;
    }

    // 未选部门且为公司级角色时，部门 ID 传 0
    const effectiveDepartmentId = selectedDepartmentId || NO_DEPARTMENT_ID;

    // editingMember 来自 getUserList 不含 sysRoleId（恒为 undefined），需用与 tempMember 相同的
    // 推导逻辑得到「原始角色」基线再比较，否则 roleChanged 恒为 true、no-op 守卫永远失效
    const baseline = initializeTempMember(editingMember);
    const departmentChanged = tempMember.departmentPath !== baseline.departmentPath;
    // 检查 sysRoleId 的变化（与推导后的基线比，String 归一化避免 number/string 类型差）
    const roleChanged = String(tempMember.sysRoleId) !== String(baseline.sysRoleId);
    // 同时检查 departmentId 是否变化
    const departmentIdChanged = String(tempMember.departmentId) !== String(baseline.departmentId);

    console.log('保存部门权限设置 - 变化检测:', {
      departmentChanged,
      departmentIdChanged,
      roleChanged,
      tempDeptPath: tempMember.departmentPath,
      editingDeptPath: editingMember.departmentPath,
      tempSysRoleId: tempMember.sysRoleId,
      editingSysRoleId: editingMember.sysRoleId,
      tempDeptId: tempMember.departmentId,
      editingDeptId: editingMember.departmentId,
    });

    // 如果部门和权限都没有变化，直接关闭对话框
    if (!departmentChanged && !departmentIdChanged && !roleChanged) {
      console.log('没有变化，直接关闭对话框');
      onCloseDeptPermission();
      return;
    }

    // 统一使用新的组合API更新部门和角色
    if (onUpdateUserDepartmentAndRole && effectiveDepartmentId && tempMember.sysRoleId) {
      const sysRoleId = tempMember.sysRoleId.toString();

      console.log('调用updateUserDepartmentAndRole API:', {
        userId: tempMember.id.toString(),
        departmentId: effectiveDepartmentId,
        sysRoleId: sysRoleId,
      });

      try {
        const success = await onUpdateUserDepartmentAndRole(
          [tempMember.id.toString()],
          effectiveDepartmentId,
          sysRoleId
        );

        console.log('API调用结果:', success);

        if (success) {
          console.log('更新成功');
          notifications.show({
            message: t('adminConsole.tip.update_success'),
            color: 'green',
          });
          // 只更新本地状态，不调用updateUser API，因为updateUserDepartmentAndRole已经更新了后端
          // onUpdateMember(tempMember); // 这会调用updateUser API，已经不需要了
        } else {
          console.log('更新失败');
        }
      } catch (error) {
        console.error('调用API时出错:', error);
      }
    } else {
      console.log('条件不满足，不调用API:', {
        hasApiMethod: !!onUpdateUserDepartmentAndRole,
        hasDeptId: !!effectiveDepartmentId,
        hasSysRoleId: !!tempMember.sysRoleId,
        tempMember,
      });
    }

    onCloseDeptPermission();
  };

  // 重置密码
  const handleConfirmPasswordReset = async () => {
    if (!editingMember) return;

    // 如果提供了重置密码的方法，使用它（会调用API）
    if (onResetPassword) {
      const password = await onResetPassword(editingMember.id.toString());
      if (password) {
        setNewPassword(password);
        notifications.show({
          title: t('adminConsole.newConsole.member.password.resetSuccess'),
          message: t('adminConsole.newConsole.member.password.generatedMessage'),
          color: 'green',
        });
      }
    } else {
      // 如果没有提供方法，只生成密码（向后兼容）
      const generatedPassword =
        Math.random().toString(36).slice(-8).toUpperCase() + Math.random().toString(36).slice(-4);
      setNewPassword(generatedPassword);
      notifications.show({
        title: t('adminConsole.newConsole.member.password.generated'),
        message: t('adminConsole.newConsole.member.password.manualUpdate'),
        color: 'yellow',
      });
    }
  };

  return (
    <>
      {/* 编辑成员信息对话框 */}
      <Modal
        opened={showEditMemberDialog}
        onClose={onCloseEditMember}
        title={t('adminConsole.newConsole.member.editTitle')}
      >
        {tempMember && (
          <Stack>
            <TextInput
              label={t('adminConsole.newConsole.member.name')}
              value={tempMember.name}
              onChange={(e) => setTempMember({ ...tempMember, name: e.currentTarget.value })}
            />
            <TextInput
              label={t('adminConsole.newConsole.member.email')}
              value={tempMember.email}
              onChange={(e) => setTempMember({ ...tempMember, email: e.currentTarget.value })}
            />
            <Group justify="flex-end">
              <Button variant="outline" onClick={onCloseEditMember}>
                {t('adminConsole.newConsole.member.cancel')}
              </Button>
              <Button onClick={handleSaveMember}>{t('adminConsole.newConsole.member.save')}</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* 状态设置对话框 */}
      <Modal opened={showStatusDialog} onClose={onCloseStatus} title={t('adminConsole.newConsole.member.statusTitle')}>
        {tempMember && (
          <Stack>
            <Card>
              <Group>
                <Avatar size="lg" color="blue">
                  {tempMember.avatar}
                </Avatar>
                <Box>
                  <Text fw={500}>{tempMember.name}</Text>
                  <Text size="sm" c="dimmed">
                    {tempMember.email}
                  </Text>
                </Box>
              </Group>
            </Card>

            <Group justify="space-between">
              <Box>
                <Text fw={500}>{t('adminConsole.newConsole.memberDialog.currentStatus')}</Text>
                <Text size="sm" c="dimmed">
                  {tempMember.enabled
                    ? t('adminConsole.newConsole.member.status.enabledDesc')
                    : t('adminConsole.newConsole.member.status.disabledDesc')}
                </Text>
              </Box>
              <Badge size="lg" color={tempMember.enabled ? 'green' : 'orange'}>
                {tempMember.enabled
                  ? t('adminConsole.newConsole.member.status.enabled') || 'Enabled'
                  : t('adminConsole.newConsole.member.status.disabled') || 'Disabled'}
              </Badge>
            </Group>

            <Divider />

            <Alert icon={<IconInfoCircle size={16} />}>
              {tempMember.enabled
                ? t('adminConsole.newConsole.memberDialog.suspendWarning')
                : t('adminConsole.newConsole.memberDialog.enableWarning')}
            </Alert>

            <Group justify="flex-end">
              <Button variant="outline" onClick={onCloseStatus}>
                {t('adminConsole.newConsole.member.cancel')}
              </Button>
              <Button
                color={tempMember.enabled ? 'red' : 'green'}
                leftSection={tempMember.enabled ? <IconCircleX size={16} /> : <IconCircleCheck size={16} />}
                onClick={handleToggleStatus}
              >
                {tempMember.enabled
                  ? t('adminConsole.newConsole.memberDialog.suspendUser')
                  : t('adminConsole.newConsole.memberDialog.enableUser')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* API绑定对话框 */}
      <Modal
        opened={showApiBindingDialog}
        onClose={onCloseApiBinding}
        title={t('adminConsole.newConsole.memberDialog.apiKeyManagement')}
        size="lg"
      >
        {tempMember && (
          <Stack>
            <Card>
              <Group>
                <Avatar size="lg" color="blue">
                  {tempMember.avatar}
                </Avatar>
                <Box>
                  <Text fw={500}>{tempMember.name}</Text>
                  <Text size="sm" c="dimmed">
                    {tempMember.email}
                  </Text>
                </Box>
              </Group>
            </Card>

            <Group justify="space-between">
              <Text fw={500}>{t('adminConsole.newConsole.memberDialog.currentApiKeys')}</Text>
              <Badge color={tempMember.apiKeyBound ? 'green' : 'gray'}>
                {tempMember.apiKeyBound
                  ? `${t('adminConsole.newConsole.member.apiKey.bound')} (${tempMember.apiKeys?.length || 0})`
                  : t('adminConsole.newConsole.member.apiKey.notBound')}
              </Badge>
            </Group>

            <ScrollArea h={200} style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: 8 }}>
              {tempMember.apiKeys && tempMember.apiKeys.length > 0 ? (
                <Stack gap="xs">
                  {tempMember.apiKeys.map((apiKey: string, index: number) => (
                    <Group key={index} p="xs" style={{ backgroundColor: '#f9fafb', borderRadius: 4 }}>
                      <Text size="sm" style={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                        {apiKey}
                      </Text>
                      <CopyButton value={apiKey}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={
                              copied
                                ? t('adminConsole.newConsole.memberDialog.copied')
                                : t('adminConsole.newConsole.memberDialog.copy')
                            }
                          >
                            <ActionIcon variant="subtle" onClick={copy} color={copied ? 'green' : 'gray'}>
                              <IconCopy size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                      <Tooltip label={t('adminConsole.newConsole.memberDialog.delete')}>
                        <ActionIcon variant="subtle" color="red" onClick={() => handleRemoveApiKey(apiKey)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" size="sm" style={{ textAlign: 'center', padding: 40 }}>
                  {t('adminConsole.newConsole.memberDialog.noApiKeys')}
                </Text>
              )}
            </ScrollArea>

            <Divider />

            <Box>
              <Text fw={500} mb="xs">
                {t('adminConsole.newConsole.memberDialog.addNewApiKey')}
              </Text>
              <Group>
                <TextInput
                  placeholder={t('adminConsole.newConsole.memberDialog.apiKeyPlaceholder')}
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newApiKey.trim()) {
                      handleAddApiKey();
                    }
                  }}
                />
                <Button loading={loadingApiKeys} onClick={handleAddApiKey} disabled={!newApiKey.trim()}>
                  {t('adminConsole.newConsole.memberDialog.add')}
                </Button>
              </Group>
            </Box>

            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              <Text size="xs">{t('adminConsole.newConsole.memberDialog.apiKeyNote')}</Text>
            </Alert>

            <Group justify="flex-end">
              <Button onClick={onCloseApiBinding}>{t('adminConsole.newConsole.memberDialog.close')}</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* 部门权限设置对话框 */}
      <Modal
        opened={showDeptPermissionDialog}
        onClose={onCloseDeptPermission}
        title={t('adminConsole.newConsole.member.deptPermissionTitle')}
      >
        {tempMember && (
          <Stack>
            <Card>
              <Group>
                <Avatar size="lg" color="blue">
                  {tempMember.avatar}
                </Avatar>
                <Box>
                  <Text fw={500}>{tempMember.name}</Text>
                  <Text size="sm" c="dimmed">
                    {tempMember.email}
                  </Text>
                </Box>
              </Group>
            </Card>

            <Box>
              <Text fw={500} mb="xs">
                {t('adminConsole.newConsole.memberDialog.departmentPath')}
              </Text>
              <Box p="sm" style={{ backgroundColor: '#f9fafb', borderRadius: 4 }}>
                <Text size="sm">{translateDepartmentPath(tempMember.departmentPath, t)}</Text>
              </Box>
            </Box>

            <Select
              label={t('adminConsole.newConsole.member.department')}
              placeholder={t('adminConsole.newConsole.member.selectDepartment')}
              value={tempMember.departmentPath === '__UNASSIGNED__' ? '' : tempMember.departmentPath || ''}
              required={!editDepartmentOptional}
              onChange={(value) => {
                if (value) {
                  const department = filteredDepartments.find((d) => d.path === value);
                  if (department) {
                    const deptName = value.split(' > ').pop() || value;
                    setTempMember({
                      ...tempMember,
                      departmentPath: value,
                      department: deptName,
                      departmentId: department.id,
                    });
                  }
                } else {
                  // 清除部门（公司级角色允许无部门）
                  setTempMember({
                    ...tempMember,
                    departmentPath: '',
                    department: '',
                    departmentId: '',
                  });
                }
              }}
              data={filteredDepartments.map((d) => ({
                value: d.path,
                label: d.path,
              }))}
              searchable
              clearable={editDepartmentOptional}
            />

            <Select
              label={t('adminConsole.newConsole.member.permissionLevel')}
              value={tempMember.sysRoleId?.toString()}
              disabled={loadingAssignableRoles}
              placeholder={
                loadingAssignableRoles
                  ? t('adminConsole.newConsole.common.loadingRoles')
                  : t('adminConsole.newConsole.common.selectRolePlaceholder')
              }
              required
              onChange={(value) => {
                if (value) {
                  const selectedRole = assignableRolesForEdit.find((r) => r.id.toString() === value);
                  if (selectedRole) {
                    setTempMember({
                      ...tempMember,
                      sysRoleId: selectedRole.id,
                      permissionText: getPermissionText(selectedRole.code, t),
                    });
                  }
                }
              }}
              data={assignableRolesForEdit.map((role) => ({
                value: role.id.toString(),
                label: getPermissionText(role.code, t),
              }))}
            />

            <Alert icon={<IconInfoCircle size={16} />}>
              <Text size="sm" fw={500}>
                {t('adminConsole.newConsole.memberDialog.permissionNote')}
              </Text>
              <Text size="xs">• {t('adminConsole.newConsole.permissions.ownerDesc')}</Text>
              <Text size="xs">• {t('adminConsole.newConsole.permissions.companyAdminDesc')}</Text>
              <Text size="xs">• {t('adminConsole.newConsole.permissions.departmentAdminDesc')}</Text>
              <Text size="xs">• {t('adminConsole.newConsole.permissions.editorDesc')}</Text>
              <Text size="xs">• {t('adminConsole.newConsole.permissions.viewerDesc')}</Text>
              {permissionControl.isDeptAdmin && !permissionControl.isAdmin && (
                <Text size="xs" c="orange" mt="xs">
                  {t('adminConsole.newConsole.memberDialog.deptAdminNote')}
                </Text>
              )}
            </Alert>

            <Group justify="flex-end">
              <Button variant="outline" onClick={onCloseDeptPermission}>
                {t('adminConsole.newConsole.member.cancel')}
              </Button>
              <Button onClick={handleSaveDeptPermission}>
                {t('adminConsole.newConsole.memberDialog.saveSettings')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* 密码重置对话框 */}
      <Modal
        opened={showPasswordResetDialog}
        onClose={() => {
          onClosePasswordReset();
          setNewPassword('');
          setShowPassword(false);
        }}
        title={t('adminConsole.newConsole.member.passwordResetTitle')}
      >
        {editingMember && (
          <Stack>
            <Card>
              <Group>
                <Avatar size="lg" color="blue">
                  {editingMember.avatar}
                </Avatar>
                <Box>
                  <Text fw={500}>{editingMember.name}</Text>
                  <Text size="sm" c="dimmed">
                    {editingMember.email}
                  </Text>
                </Box>
              </Group>
            </Card>

            {!newPassword && (
              <Alert icon={<IconRotateClockwise size={16} />} color="yellow">
                <Text size="sm" fw={500}>
                  {t('adminConsole.newConsole.memberDialog.passwordResetConfirm')}
                </Text>
                <Text size="xs">{t('adminConsole.newConsole.memberDialog.passwordResetActions')}</Text>
                <Text size="xs">• {t('adminConsole.newConsole.memberDialog.generateTempPassword')}</Text>
                <Text size="xs">• {t('adminConsole.newConsole.memberDialog.invalidateCurrentPassword')}</Text>
                <Text size="xs">• {t('adminConsole.newConsole.memberDialog.requireRelogin')}</Text>
                <Text size="xs">• {t('adminConsole.newConsole.memberDialog.suggestChangePassword')}</Text>
              </Alert>
            )}

            {newPassword && (
              <Stack>
                <Alert icon={<IconCircleCheck size={16} />} color="green">
                  <Text size="sm" fw={500}>
                    {t('adminConsole.newConsole.memberDialog.passwordResetSuccess')}
                  </Text>
                  <Text size="xs">{t('adminConsole.newConsole.memberDialog.tempPasswordGenerated')}</Text>
                </Alert>

                <Box>
                  <Text fw={500} mb="xs">
                    {t('adminConsole.newConsole.memberDialog.newPassword')}
                  </Text>
                  <Group>
                    <PasswordInput
                      value={newPassword}
                      readOnly
                      style={{ flex: 1 }}
                      visible={showPassword}
                      onVisibilityChange={setShowPassword}
                    />
                    <CopyButton value={newPassword}>
                      {({ copied, copy }) => (
                        <Button variant="outline" onClick={copy}>
                          {copied ? <IconCheck size={18} color="teal" /> : <IconCopy size={18} color="gray" />}
                        </Button>
                      )}
                    </CopyButton>
                  </Group>
                  <Text size="xs" c="dimmed" mt="xs">
                    {t('adminConsole.newConsole.memberDialog.passwordSaveNote')}
                  </Text>
                </Box>
              </Stack>
            )}

            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={() => {
                  onClosePasswordReset();
                  setNewPassword('');
                  setShowPassword(false);
                }}
              >
                {newPassword
                  ? t('adminConsole.newConsole.member.complete')
                  : t('adminConsole.newConsole.member.cancel')}
              </Button>
              {!newPassword && (
                <Button
                  color="red"
                  leftSection={<IconRotateClockwise size={16} />}
                  onClick={handleConfirmPasswordReset}
                >
                  {t('adminConsole.newConsole.memberDialog.confirmReset')}
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
};

export default MemberEditDialogs;
